import { Link } from "react-router-dom";
import { LegalTranslationNote } from "@/components/LegalTranslationNote";
import { ScrollText } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    body:
      "By downloading, installing, or using the Generational Health mobile application or the website at generationalh.com (collectively, the \"Services\"), you agree to be bound by these Terms of Service (\"Terms\"). If you do not agree, do not use the Services. These Terms apply to all users — including patients, caregivers, providers, donors, and partner organizations.",
  },
  {
    title: "2. Eligibility",
    body:
      "You must be at least 13 years old to use the Services. Users between 13 and 17 must have verifiable parental or guardian consent (handled through our DocuSign workflow), unless you qualify as an emancipated minor under applicable state law and complete the corresponding self-consent flow. Providers must hold an active, unrestricted license in the U.S. state(s) where they deliver care.",
  },
  {
    title: "3. Not a Substitute for Emergency Care",
    body:
      "Generational Health is not an emergency service. If you are experiencing a medical emergency, in immediate danger of harming yourself or others, or otherwise require urgent care, call 911 or the 988 Suicide & Crisis Lifeline immediately. The AI Companion, peer supporters, and licensed providers within the Services are not a replacement for emergency medical, psychiatric, or law-enforcement services.",
  },
  {
    title: "4. Clinical Services & Provider Relationships",
    body:
      "Licensed providers offering clinical services through the Services do so under their own license and are solely responsible for the care they provide. Generational Health acts as a technology platform connecting you with providers and does not itself practice medicine, nursing, psychotherapy, or pharmacy. Provider availability, scope of practice, and accepted insurance vary by state.",
  },
  {
    title: "5. Insurance Verification (pVerify)",
    body:
      "We use pVerify to verify Medicaid, Medicare, and commercial insurance eligibility. Eligibility results are provided by the carrier and may not reflect the final adjudication of any claim. You remain responsible for any patient cost-share, copay, deductible, coinsurance, or non-covered amount that becomes due.",
  },
  {
    title: "6. E-Prescribing (DoseSpot & Drug Bank)",
    body:
      "Prescriptions are issued by licensed providers through DoseSpot's certified e-prescribing pipeline. Drug Bank provides medication information for educational purposes only and is not a substitute for clinical advice from your prescriber or pharmacist. Always consult your provider before starting, stopping, or changing any medication.",
  },
  {
    title: "7. AI Companion",
    body:
      "The AI Companion provides coping skill education, journaling support, and triage to human providers. It is not a licensed clinician, does not diagnose, and is not a substitute for professional care. Conversations with the AI Companion may be reviewed by clinical staff for safety, training, and quality improvement, consistent with our Privacy Policy.",
  },
  {
    title: "8. Consent & Records (DocuSign)",
    body:
      "Consent forms — including for minors and emancipated youth — are executed through DocuSign and stored as part of your medical record. You are responsible for the accuracy of any information submitted during consent flows. False or fraudulent consent may result in account termination and could constitute a violation of state law.",
  },
  {
    title: "9. Donations",
    body:
      "Donations made through the Services are processed by our payment processor and are not refundable except where required by law. Donations are not in exchange for goods or services and may be tax-deductible to the extent allowed by law; consult your tax advisor.",
  },
  {
    title: "10. Acceptable Use",
    body:
      "You agree not to: (a) use the Services to threaten or harm others; (b) impersonate any person or entity; (c) interfere with or disrupt the Services; (d) attempt to gain unauthorized access to any system or data; (e) use automated tools to scrape or harvest content; or (f) misrepresent your identity, eligibility, or clinical status. Violations may result in immediate suspension or termination.",
  },
  {
    title: "11. Intellectual Property",
    body:
      "All content, software, design, AI models, the patented provider backflow workflow, and the Generational Health name and logo are owned by Generational Health or its licensors. You receive a limited, revocable, non-transferable license to use the Services for their intended purpose.",
  },
  {
    title: "12. Privacy & HIPAA",
    body:
      "Your use of the Services is also governed by our Privacy Policy and our Notice of Privacy Practices. Generational Health complies with HIPAA. We have Business Associate Agreements with our integrated vendors (pVerify, DoseSpot, Drug Bank, DocuSign) and treat all Protected Health Information accordingly.",
  },
  {
    title: "13. Disclaimers & Limitation of Liability",
    body:
      "The Services are provided \"as is\" and \"as available.\" To the maximum extent permitted by law, Generational Health disclaims all warranties, express or implied. To the maximum extent permitted by law, our aggregate liability for any claim arising out of or relating to the Services shall not exceed one hundred dollars ($100) or the amount you paid us in the prior twelve months, whichever is greater. This limitation does not apply to liability arising from gross negligence, willful misconduct, or any liability that cannot be limited under applicable law.",
  },
  {
    title: "14. Termination",
    body:
      "You may stop using the Services at any time. We may suspend or terminate your access for any violation of these Terms, suspected fraud, regulatory requirements, or risk to safety. We will retain medical records as required by applicable law.",
  },
  {
    title: "15. Governing Law",
    body:
      "These Terms are governed by the laws of the State of North Carolina, without regard to conflict of laws principles. Any dispute arising from these Terms or the Services will be resolved in the state or federal courts located in Mecklenburg County, North Carolina, except where applicable consumer-protection law gives you a different right.",
  },
  {
    title: "16. Changes to These Terms",
    body:
      "We may update these Terms from time to time. Material changes will be posted with an updated effective date and, where appropriate, notified through the app. Your continued use of the Services after changes take effect constitutes acceptance.",
  },
  {
    title: "17. Contact",
    body:
      "Questions about these Terms? Email us at legal@generationalh.com or write to: Generational Health, Charlotte, NC.",
  },
];

const EFFECTIVE_DATE = "January 15, 2026";

export default function Terms() {
  return (
    <div data-testid="terms-page" className="bg-white">
      <section className="py-16 lg:py-20 bg-gradient-hero" data-testid="terms-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-teal-light text-teal flex items-center justify-center">
              <ScrollText className="w-6 h-6" />
            </div>
            <p className="eyebrow">Legal</p>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">Terms of Service</h1>
          <p className="mt-5 text-muted-ink">Effective: {EFFECTIVE_DATE}</p>
          <p className="mt-3 text-muted-ink text-[15px] leading-relaxed">
            These Terms govern your use of the Generational Health mobile app and website. Please read carefully.
          </p>
          <LegalTranslationNote />
        </div>
      </section>

      <section className="py-16 lg:py-20" data-testid="terms-content">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 space-y-10">
          {SECTIONS.map((s, i) => (
            <div key={i} data-testid={`terms-section-${i}`}>
              <h2 className="font-display text-2xl text-navy mb-3">{s.title}</h2>
              <p className="text-[16px] text-muted-ink leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12 bg-cream" data-testid="terms-footer-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center text-sm text-muted-ink">
          See also: <Link to="/privacy" className="text-teal font-bold hover:underline">Privacy Policy</Link> · <Link to="/contact" className="text-teal font-bold hover:underline">Contact</Link>
        </div>
      </section>
    </div>
  );
}
